import { I18nTextSchema, type JsonObject } from '@inf-monkeys-tech/monkeys';
import { useMonkeysLocale } from '../../provider/context';
import type { CapabilityTranslate, CapabilityTranslateValues } from './contracts';

const localePattern = '^[a-z]{2,3}(?:-[A-Z][a-z]{3})?(?:-[A-Z]{2}|-\\d{3})?$';
/** Structural schema; SDK I18nTextSchema also enforces that values contains defaultLocale. */
export const I18N_DISPLAY_TEXT_SCHEMA: JsonObject = {
  type: 'object', additionalProperties: false, required: ['defaultLocale', 'values'],
  properties: {
    defaultLocale: { type: 'string', pattern: localePattern },
    values: { type: 'object', minProperties: 1, propertyNames: { pattern: localePattern }, additionalProperties: { type: 'string', minLength: 1, pattern: '\\S' } },
  },
};
export const LOCALIZED_DISPLAY_PARAMETERS_SCHEMA: JsonObject = {
  type: 'object', additionalProperties: { oneOf: [{ type: ['string', 'number', 'boolean'] }, I18N_DISPLAY_TEXT_SCHEMA] },
};

/** I18nText only: LocalizedText locale maps and plain strings are deliberately distinct. */
export function resolveI18nDisplayText(value: unknown, locale: string): string {
  const parsed = I18nTextSchema.safeParse(value);
  if (!parsed.success) throw new TypeError('Invalid I18n display text');
  let canonical: string;
  try { canonical = Intl.getCanonicalLocales(locale)[0] ?? ''; } catch { canonical = ''; }
  const language = canonical.split('-')[0] ?? '';
  return parsed.data.values[canonical] ?? parsed.data.values[language] ?? parsed.data.values[parsed.data.defaultLocale]!;
}

export function LocalizedDisplayText({ value }: { value: unknown }) {
  const { locale } = useMonkeysLocale();
  return <>{resolveI18nDisplayText(value, locale)}</>;
}

export function resolveLocalizedDisplayParameters(value: unknown, locale: string): CapabilityTranslateValues | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'object' || value === null || Array.isArray(value) ||
      ![Object.prototype, null].includes(Object.getPrototypeOf(value))) throw new TypeError('Invalid localized interpolation parameters');
  return Object.fromEntries(Object.entries(value).map(([key, item]) => {
    if (typeof item === 'string' || typeof item === 'boolean' || (typeof item === 'number' && Number.isFinite(item))) return [key, item];
    return [key, resolveI18nDisplayText(item, locale)];
  }));
}

export function LocalizedMessageText({ messageKey, values, translate }: { messageKey: string; values?: unknown; translate: CapabilityTranslate }) {
  const { locale } = useMonkeysLocale();
  return <>{translate(messageKey, resolveLocalizedDisplayParameters(values, locale))}</>;
}
